import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Portal from 'Core/components/Portal';

/**
 * TermsModal - Modal for displaying EasyCredit data transfer information
 * All content loaded from locize translations
 * Close button only enabled after scrolling to end (or if no scroll needed)
 */
const TermsModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [canClose, setCanClose] = useState(false);
  const contentRef = useRef(null);
  const scrollPositionRef = useRef(0);

  // Check if user has scrolled to bottom or content doesn't need scrolling
  const checkScrollPosition = useCallback(() => {
    const element = contentRef.current;
    if (!element) return;

    const { scrollTop, scrollHeight, clientHeight } = element;
    
    // Save current scroll position
    scrollPositionRef.current = scrollTop;
    
    // Allow 10px threshold for "close enough" to bottom
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
    // If content doesn't need scrolling, enable close immediately
    const noScrollNeeded = scrollHeight <= clientHeight;

    if (isAtBottom || noScrollNeeded) {
      // Only update state if not already true (prevents re-render loop)
      setCanClose((prev) => {
        if (prev) return prev; // Already true, don't change
        return true;
      });
    }
  }, []);

  // Restore scroll position after render (in case of re-mount)
  useEffect(() => {
    if (contentRef.current && scrollPositionRef.current > 0) {
      contentRef.current.scrollTop = scrollPositionRef.current;
    }
  });

  // Reset canClose when modal opens and check initial state
  useEffect(() => {
    if (isOpen) {
      setCanClose(false);
      // Check after a short delay to allow content to render
      setTimeout(checkScrollPosition, 100);
    }
  }, [isOpen, checkScrollPosition]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle escape key to close modal (only if canClose)
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && canClose) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, canClose]);

  if (!isOpen) return null;

  // Content sections from locize
  const sections = [
    { key: 'dataTransferred' },
    { key: 'dataPurpose' },
    { key: 'legalBasis' },
    { key: 'storageDuration' },
    { key: 'responsibleParty' },
    { key: 'dataSharing' },
  ];

  return (
    <Portal>
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
      >
        {/* Backdrop - only closable after scroll */}
        <div 
          className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
          onClick={canClose ? onClose : undefined}
        />
        
        {/* Modal */}
        <div className="relative w-full max-w-5xl max-h-[85vh] bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 bg-blue-600 text-white">
            <h2 className="text-lg font-bold pr-4">
              {t('easyCredit.termsModal.title')}
            </h2>
            <button
              onClick={canClose ? onClose : undefined}
              disabled={!canClose}
              className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                canClose 
                  ? 'bg-white/20 hover:bg-white/30 cursor-pointer' 
                  : 'bg-white/10 cursor-not-allowed opacity-50'
              }`}
              aria-label="Schließen"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Content */}
          <div 
            ref={contentRef}
            onScroll={checkScrollPosition}
            className="flex-1 overflow-y-auto px-5 py-5"
          >
            {/* Introduction */}
            <p className="text-sm text-gray-700 leading-relaxed mb-5">
              {t('easyCredit.termsModal.intro')}
            </p>
            
            {/* Sections */}
            {sections.map((section) => (
              <div key={section.key} className="mb-5 last:mb-0">
                <h3 className="text-sm font-bold text-gray-900 mb-2">
                  {t(`easyCredit.termsModal.${section.key}.title`)}
                </h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  {t(`easyCredit.termsModal.${section.key}.content`)}
                </p>
              </div>
            ))}
          </div>
          
          {/* Footer */}
          <div className="px-5 py-4 bg-gray-50 border-t">
            {!canClose && (
              <p className="text-xs text-orange-600 font-medium text-center mb-2 flex items-center justify-center gap-1">
                <svg className="w-4 h-4 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
                {t('easyCredit.termsModal.scrollHint')}
              </p>
            )}
            <button
              onClick={canClose ? onClose : undefined}
              disabled={!canClose}
              className={`w-full py-2.5 font-semibold rounded-lg transition-all ${
                canClose
                  ? 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {t('easyCredit.termsModal.closeButton')}
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default TermsModal;
